import { Classes, Dialog, Spinner, Tab, Tabs } from "@blueprintjs/core";
import { Search } from "modules/common/components/form/Search";
import { ItemListPredicate } from "@blueprintjs/select";
import { observer } from "mobx-react";
import { getSnapshot } from "mobx-state-tree";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { DefaultSelectedOption, renderSingleOption, SimpleSelect } from "modules/common/services/form/select";
import React, { useEffect, useMemo, useState } from "react";
import { NO_DEPARTMENT_NAME } from "modules/spending/employee/models/employer";
import {
  OneCEntityMappingSnapshotType,
  OneCMappingSnapshotType,
  OneCPreviewItem,
  OneCStoreType,
} from "../models/onec-store";
import styles from "./OneCImportDialog.module.scss";

export interface MappingRole {
  key: string;
  label: string;
}

// Пресеты ролей полей для сопоставления (структура организации).
export const POSITION_ROLES: MappingRole[] = [
  { key: "Name", label: "Наименование" },
  { key: "ExternalId", label: "Идентификатор 1С (Ref_Key)" },
  { key: "DeletionMark", label: "Признак удаления (архив)" },
];

// у подразделений ещё и родитель: из него строится дерево структуры
export const DEPARTMENT_ROLES: MappingRole[] = [
  ...POSITION_ROLES,
  { key: "Parent", label: "Родитель (иерархия, Parent_Key)" },
];

// Сотрудники — составная сущность; в настройках задаём минимум (остальное собирается ридером из связанных ресурсов).
export const EMPLOYEE_ROLES: MappingRole[] = [
  { key: "Name", label: "ФИО (представление)" },
  { key: "ExternalId", label: "Идентификатор 1С (Ref_Key)" },
  { key: "Archived", label: "Поле-флаг увольнения/архива" },
];

// Штатное расписание: позиции «подразделение + должность + ставки»; подразделения и должности ищутся
// по уже импортированным из 1С, поэтому их нужно импортировать раньше.
export const STAFF_ROLES: MappingRole[] = [
  { key: "Unit", label: "Подразделение (ссылка)" },
  { key: "Position", label: "Должность (ссылка)" },
  { key: "Count", label: "Количество ставок" },
  { key: "Closed", label: "Признак «позиция закрыта»" },
  { key: "Name", label: "Наименование позиции" },
  { key: "DeletionMark", label: "Признак удаления" },
];

type EntityKey = "departments" | "positions" | "employees" | "staff";

// Легенда над таблицей: что значит статус и что сделает импорт с отмеченной строкой (сверено с ApplyOneCImport)
const LEGEND: Record<EntityKey, { status: string; text: string }[]> = {
  employees: [
    { status: "new", text: "будет создан сотрудник с должностью и отделом из 1С или выбранными в строке." },
    {
      status: "candidate",
      text: "в приложении есть сотрудник с таким же ФИО, он будет связан с 1С; данные, должность и отдел не меняются.",
    },
    { status: "linked", text: "уже связан с 1С, импорт ничего не меняет." },
    { status: "onlyInRost", text: "есть только в приложении; выберите запись 1С, чтобы связать, иначе импорт не трогает." },
  ],
  departments: [
    { status: "new", text: "будет создан отдел." },
    {
      status: "candidate",
      text: "в приложении есть отдел с таким же названием, он будет связан с 1С; место в дереве станет как в 1С.",
    },
    { status: "linked", text: "уже связан с 1С: название и место в дереве обновятся по 1С." },
    { status: "onlyInRost", text: "есть только в приложении; выберите запись 1С, чтобы связать, иначе импорт не трогает." },
  ],
  positions: [
    { status: "new", text: "будет создана должность в отделе «Без отдела», отдел потом указывают в карточке." },
    {
      status: "candidate",
      text: "в приложении есть должность с таким же названием, она будет связана с 1С; название и отдел не меняются.",
    },
    { status: "linked", text: "уже связана с 1С, импорт ничего не меняет." },
    { status: "onlyInRost", text: "есть только в приложении; выберите запись 1С, чтобы связать, иначе импорт не трогает." },
  ],
  staff: [
    { status: "new", text: "должность будет добавлена в штат подразделения с числом ставок из 1С." },
    { status: "candidate", text: "число ставок в штате заменится на значение из 1С." },
    { status: "linked", text: "число ставок уже такое же, по умолчанию не отмечено." },
    {
      status: "unresolved",
      text: "подразделение или должность не связаны с 1С, сначала импортируйте их. Позиции 1С одной пары складываются, дробь округляется вверх; остальной штат не меняется.",
    },
  ],
};

interface OneCImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  store: OneCStoreType;
  entityKey: EntityKey;
  title: string;
  roles: MappingRole[];
  onImported?: () => void;
}

// у должностей - женский род: «Новая», «Уже импортирована»
const POSITION_STATUS_LABEL: TStringMap<string> = {
  new: "Новая",
  linked: "Уже импортирована",
  candidate: "Связать с 1С",
  onlyInRost: "Нет в 1С",
};

const STATUS_LABEL: TStringMap<string> = {
  new: "Новый",
  linked: "Уже импортирован",
  candidate: "Связать с 1С",
  onlyInRost: "Нет в 1С",
};

// у штата статусы значат другое: строка — пара «подразделение + должность», сравнивается число ставок
const STAFF_STATUS_LABEL: TStringMap<string> = {
  new: "Новая ставка",
  linked: "Совпадает",
  candidate: "Изменится",
  onlyInRost: "Нет в 1С",
};

const STATUS_CLASS: TStringMap<string> = {
  new: "statusNew",
  linked: "statusLinked",
  candidate: "statusCandidate",
  onlyInRost: "statusOnlyRost",
};

// Ограничиваем крупный список каталогов (в 1С их тысячи), чтобы поповер не тормозил.
const limitList = (max: number): ItemListPredicate<SelectItem> => (query, items) => {
  const q = query.toLowerCase().trim();
  const filtered = q ? items.filter((i) => i.label.toLowerCase().indexOf(q) >= 0) : items;
  return filtered.slice(0, max);
};
const limitResources = limitList(200);
const limitFields = limitList(200);
const limitBind = limitList(300);

const toItems = (names: string[]): SelectItem[] => names.map((n) => ({ id: n, label: n }));
const findItem = (items: SelectItem[], id: string | undefined) => items.find((i) => i.id === id) || null;

const OneCImportDialogBase: React.FC<OneCImportDialogProps> = ({
  isOpen,
  onClose,
  store,
  entityKey,
  title,
  roles,
  onImported,
}) => {
  const [tab, setTab] = useState<string>("settings");
  const [search, setSearch] = useState<string>("");
  // Фильтр по статусу из чипов сводки (new/linked/candidate/onlyInRost, у штата ещё unresolved); null - все
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [resource, setResource] = useState<string>("");
  const [fieldMap, setFieldMap] = useState<TStringMap<string>>({});
  // Решения по строкам: ключ — externalId. include — импортировать; targetId — привязка ("" = создать);
  // positionTargetId — должность для сотрудника ("" = заглушка).
  const [decisions, setDecisions] = useState<
    TStringMap<{ include: boolean; targetId: string; positionTargetId: string }>
  >({});

  const isEmployees = entityKey === "employees";
  const isStaff = entityKey === "staff";
  const statusLabel = isStaff
    ? STAFF_STATUS_LABEL
    : entityKey === "positions"
    ? POSITION_STATUS_LABEL
    : STATUS_LABEL;

  // Поле поиска в выпадающем списке привязки: список зависит от того, что импортируем
  const bindPlaceholder = isEmployees ? "Сотрудник" : entityKey === "positions" ? "Должность" : "Отдел";

  // При открытии — грузим каталоги и профиль сопоставления.
  useEffect(() => {
    if (isOpen) {
      setTab("settings");
      store.loadResources();
      store.loadMapping();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Когда профиль загружен — инициализируем каталог/поля. Если связи уже настроены (профиль сохранён) —
  // открываем сразу таблицу и грузим предпросмотр; иначе — вкладку «Настройки импорта».
  useEffect(() => {
    if (isOpen && store.mapping) {
      const entity = store.mapping[entityKey];
      setResource(entity.resource);
      setFieldMap({ ...entity.fieldMap });
      if (entity.resource) {
        store.loadFields(entity.resource);
      }
      if (store.mapping.saved && entity.resource) {
        setTab("preview");
        store.previewImport(entityKey);
      } else {
        setTab("settings");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entityKey, store.mapping]);

  // При получении предпросмотра — инициализируем решения: связанные/кандидаты предзаполнены привязкой,
  // новые — «создать» (targetId пустой). Строки «нет в 1С» в импорте не участвуют.
  useEffect(() => {
    const p = store.preview;
    if (p) {
      const init: TStringMap<{ include: boolean; targetId: string; positionTargetId: string }> = {};
      p.items.forEach((it) => {
        if (it.status === "onlyInRost" || !it.externalId) {
          return;
        }
        init[it.externalId] = {
          // совпадающие ставки импортировать незачем — по умолчанию не отмечаем
          include: !(isStaff && it.status === "linked"),
          targetId: it.targetId || "",
          positionTargetId: it.suggestedPositionId || "",
        };
      });
      setDecisions(init);
      setStatusFilter(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.preview]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resourceItems = useMemo(() => toItems(store.resources.slice()), [store.resources.length]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fieldItems = useMemo(() => toItems(store.fields.slice()), [store.fields.length]);

  // Варианты привязки строки: «прикрепить из приложения» (создать новую) + существующие записи приложения.
  // Для должностей в подписи показываем отдел, чтобы различать одноимённые.
  const bindItems: SelectItem[] = useMemo(() => {
    const base: SelectItem[] = [{ id: "", label: "Прикрепить из приложения" }];
    const p = store.preview;
    return p
      ? base.concat(p.rostEntities.map((e) => ({ id: e.id, label: e.group ? `${e.name} (${e.group})` : e.name })))
      : base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.preview]);

  // Подпись целевой записи приложения: в две строки — отдел (приглушённо) сверху, должность снизу.
  const renderTarget = (targetId: string | null, fallback: string | null) => {
    const entity = targetId && preview ? preview.rostEntities.find((e) => e.id === targetId) : null;
    if (!entity) {
      return <span className={styles.targetName}>{fallback || "—"}</span>;
    }
    return (
      <div className={styles.targetCell}>
        {entity.group && <div className={styles.targetGroup}>{entity.group}</div>}
        <div className={styles.targetName}>{entity.name}</div>
      </div>
    );
  };

  const emptyDecision = { include: false, targetId: "", positionTargetId: "" };

  const toggleInclude = (externalId: string) => {
    setDecisions((prev) => {
      const cur = prev[externalId] || emptyDecision;
      return { ...prev, [externalId]: { ...cur, include: !cur.include } };
    });
  };

  // «Выбрать все / Снять все» - по строкам, видимым сейчас (с учётом поиска), которые можно импортировать
  const setIncludeAll = (externalIds: string[], include: boolean) => {
    setDecisions((prev) => {
      const next = { ...prev };
      externalIds.forEach((id) => {
        next[id] = { ...(prev[id] || emptyDecision), include };
      });
      return next;
    });
  };

  const setTarget = (externalId: string, targetId: string) => {
    setDecisions((prev) => {
      const cur = prev[externalId] || { ...emptyDecision, include: true };
      return { ...prev, [externalId]: { ...cur, targetId } };
    });
  };

  // Обратная привязка из строки «Нет в 1С»: выбрали запись 1С - это то же решение, что привязка со стороны 1С
  // (строка 1С отмечается и ведёт на эту запись приложения, строка «Нет в 1С» скрывается как занятая)
  const bindFromRost = (externalId: string, targetId: string) => {
    setDecisions((prev) => {
      const cur = prev[externalId] || emptyDecision;
      return { ...prev, [externalId]: { ...cur, include: true, targetId } };
    });
  };

  const setPosition = (externalId: string, positionTargetId: string) => {
    setDecisions((prev) => {
      const cur = prev[externalId] || { ...emptyDecision, include: true };
      return { ...prev, [externalId]: { ...cur, positionTargetId } };
    });
  };

  // Список должностей приложения для сотрудника: заглушка + существующие (с отделом в подписи).
  const positionItems: SelectItem[] = useMemo(() => {
    const base: SelectItem[] = [{ id: "", label: "Без должности (заглушка)" }];
    const p = store.preview;
    return p
      ? base.concat(p.positions.map((e) => ({ id: e.id, label: e.group ? `${e.name} (${e.group})` : e.name })))
      : base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.preview]);

  // Записи 1С для обратной привязки: ещё ни к чему не привязанные (создадутся как новые)
  const sourceItems: SelectItem[] = (store.preview ? store.preview.items : [])
    .filter((it) => it.externalId && it.status !== "onlyInRost" && !it.problem)
    .filter((it) => !(decisions[it.externalId as string] && decisions[it.externalId as string].targetId))
    .map((it) => ({
      id: it.externalId as string,
      label: isEmployees && it.birthday ? `${it.sourceName} (${it.birthday})` : it.sourceName || "",
    }));

  // Отдел нового сотрудника - как при импорте: из кадровой истории 1С, иначе отдел выбранной должности
  // (у заглушки - «Без отдела»). Без отдела карточку потом правят вручную - предупреждаем заранее.
  const renderUnit = (item: OneCPreviewItem, positionTargetId: string) => {
    const position = positionTargetId
      ? store.preview?.positions.find((p) => p.id === positionTargetId)
      : undefined;
    const unit = item.departmentName || position?.group || "";

    return unit && unit !== NO_DEPARTMENT_NAME ? (
      <div className={styles.targetGroup}>Отдел: {unit}</div>
    ) : (
      <div className={styles.unitMissing}>
        Отдел не найден — будет «{NO_DEPARTMENT_NAME}», выберите должность с отделом или укажите отдел в карточке
      </div>
    );
  };

  // Привязка к сотруднику приложения должность и отдел не меняет - показываем его нынешние, без выбора
  const renderCurrentPosition = (targetId: string) => {
    const entity = preview ? preview.rostEntities.find((e) => e.id === targetId) : undefined;
    if (!entity || (!entity.positionName && !entity.departmentName)) {
      return <span className={styles.targetName}>—</span>;
    }
    return (
      <div className={styles.targetCell}>
        {entity.departmentName && <div className={styles.targetGroup}>{entity.departmentName}</div>}
        <div className={styles.targetName}>{entity.positionName || "—"}</div>
      </div>
    );
  };

  const onResourceSelect = (item: SelectItem) => {
    setResource(item.id);
    store.loadFields(item.id);
  };

  const onFieldSelect = (roleKey: string, item: SelectItem) => {
    setFieldMap((prev) => ({ ...prev, [roleKey]: item.id }));
  };

  const buildModel = (): OneCMappingSnapshotType | null => {
    if (!store.mapping) {
      return null;
    }

    const current = getSnapshot(store.mapping) as OneCMappingSnapshotType;
    const updatedEntity: OneCEntityMappingSnapshotType = {
      resource,
      fieldMap,
      relatedResources: current[entityKey].relatedResources,
    };

    return { ...current, [entityKey]: updatedEntity };
  };

  // Применить настройки импорта: сохраняем сопоставление, сравниваем 1С с приложением и переходим к таблице.
  const doApply = async () => {
    const model = buildModel();
    if (!model) {
      return;
    }
    const saved = await store.saveMapping(model);
    if (saved) {
      await store.previewImport(entityKey);
      setTab("preview");
    }
  };

  const requiredRoles = isStaff ? ["Unit", "Position", "Count"] : ["Name", "ExternalId"];
  const applyDisabled = !resource || requiredRoles.some((r) => !fieldMap[r]) || !store.mapping || store.previewing;

  const doImport = async () => {
    const list = Object.keys(decisions)
      .filter((externalId) => decisions[externalId].include)
      .map((externalId) => ({
        externalId,
        targetId: decisions[externalId].targetId,
        positionTargetId: decisions[externalId].positionTargetId,
      }));

    if (list.length === 0) {
      return;
    }

    const ok = await store.applyImport(entityKey, list);
    if (ok) {
      onImported && onImported();
      await store.previewImport(entityKey);
    }
  };

  const selectedCount = Object.keys(decisions).filter((k) => decisions[k].include).length;

  const closeDialog = () => {
    store.clearPreview();
    setSearch("");
    setStatusFilter(null);
    onClose();
  };

  const notConfigured = !store.loadingResources && store.resources.length === 0;
  const canImport = true;
  const preview = store.preview;

  // Записи приложения, уже занятые ручной привязкой, — их строки «нет в 1С» скрываем.
  const usedTargetIds = new Set(
    Object.keys(decisions)
      .filter((k) => decisions[k].include && decisions[k].targetId)
      .map((k) => decisions[k].targetId)
  );

  // Базовый набор (без поиска) — по нему считаем сводку; поиск фильтрует только отображение.
  // новые - всегда вверху, не сопоставленные строки штата (их не импортировать) - внизу;
  // сортировка устойчивая: внутри групп порядок с сервера
  const baseItems = (preview ? preview.items : [])
    .filter((it) => !(it.status === "onlyInRost" && it.targetId && usedTargetIds.has(it.targetId)))
    .map((it, index) => ({ it, first: it.problem ? 2 : it.status === "new" ? 0 : 1, index }))
    .sort((a, b) => a.first - b.first || a.index - b.index)
    .map((x) => x.it);

  // Статус по текущему выбору: строку 1С, которую вручную привязали к записи приложения, импорт свяжет, а не
  // создаст - показываем «Связать с 1С»; сняли привязку у совпавшей по имени - «Новый». У штата привязки нет
  const effectiveStatus = (it: OneCPreviewItem) => {
    if (isStaff || !it.externalId || (it.status !== "new" && it.status !== "candidate")) {
      return it.status;
    }
    return decisions[it.externalId] && decisions[it.externalId].targetId ? "candidate" : "new";
  };

  const counts = { new: 0, linked: 0, candidate: 0, onlyInRost: 0 } as TStringMap<number>;
  let unresolved = 0;
  baseItems.forEach((it) => {
    if (it.problem) {
      unresolved++;
      return;
    }
    const status = effectiveStatus(it);
    counts[status] = (counts[status] || 0) + 1;
  });

  const query = search.trim().toLowerCase();
  // строка штата без сопоставления считается отдельным статусом «Не сопоставлено»
  const statusOf = (it: OneCPreviewItem) => (it.problem ? "unresolved" : effectiveStatus(it));
  const visibleItems = baseItems.filter(
    (it) =>
      (!statusFilter || statusOf(it) === statusFilter) &&
      (!query ||
        [it.sourceName, it.targetName, it.birthday, it.suggestedPositionName].some(
          (v) => (v || "").toLowerCase().indexOf(query) >= 0
        ))
  );

  // Чип сводки - кнопка-фильтр: нажали - только этот статус, нажали ещё раз - все
  const renderChip = (status: string, className: string, label: string, count: number) => (
    <button
      type="button"
      key={status}
      className={`${styles.chip} ${className} ${statusFilter === status ? styles.chipActive : ""} ${
        statusFilter && statusFilter !== status ? styles.chipDimmed : ""
      }`}
      onClick={() => setStatusFilter(statusFilter === status ? null : status)}
      title={statusFilter === status ? "Показать все" : "Показать только эти"}
    >
      {label}: {count}
    </button>
  );

  const isImportableItem = (it: OneCPreviewItem) => it.status !== "onlyInRost" && !!it.externalId;
  const selectableIds = visibleItems.filter(isImportableItem).map((it) => it.externalId as string);
  const selectedVisible = selectableIds.filter((id) => decisions[id] && decisions[id].include).length;
  const allVisibleSelected = selectableIds.length > 0 && selectedVisible === selectableIds.length;

  // Должности/отделы приложения не связаны с 1С - должность и отдел из кадровой истории никому не подставятся
  const missingLinks =
    isEmployees && preview
      ? [
          !preview.linkedDepartmentsCount ? { gen: "подразделений", acc: "подразделения" } : null,
          !preview.linkedPositionsCount ? { gen: "должностей", acc: "должности" } : null,
        ].filter((v): v is { gen: string; acc: string } => !!v)
      : [];
  const newWithoutPosition =
    isEmployees && preview ? baseItems.filter((it) => it.status === "new" && !it.suggestedPositionId).length : 0;

  const mappingPanel = (
    <div className={styles.mappingPanel}>
      <div className={styles.label}>Каталог 1С</div>
      <SimpleSelect
        className={`full-width-select ${Classes.FILL}`}
        activeItem={findItem(resourceItems, resource)}
        items={resourceItems}
        itemRenderer={renderSingleOption}
        onItemSelect={onResourceSelect}
        popoverProps={{ rootBoundary: "viewport" }}
        inputProps={{ placeholder: "Каталог" }}
        filterable={true}
        itemListPredicate={limitResources}
      >
        <DefaultSelectedOption option={findItem(resourceItems, resource)} what="Выберите каталог" />
      </SimpleSelect>

      {resource && (
        <div className={styles.fields}>
          <div className={styles.label} style={{ marginTop: "16px" }}>
            Сопоставление полей
            {store.loadingFields && <Spinner size={16} className={styles.inlineSpinner} />}
          </div>

          {roles.map((role) => (
            <div key={role.key} className={styles.fieldRow}>
              <div className={styles.roleLabel}>{role.label}</div>
              <SimpleSelect
                className={`full-width-select ${Classes.FILL}`}
                activeItem={findItem(fieldItems, fieldMap[role.key])}
                items={fieldItems}
                itemRenderer={renderSingleOption}
                onItemSelect={(item: SelectItem) => onFieldSelect(role.key, item)}
                popoverProps={{ rootBoundary: "viewport" }}
                inputProps={{ placeholder: "Поле 1С" }}
                filterable={true}
                itemListPredicate={limitFields}
              >
                <DefaultSelectedOption option={findItem(fieldItems, fieldMap[role.key])} what="Не задано" />
              </SimpleSelect>
            </div>
          ))}
        </div>
      )}

      <div className={styles.applyRow}>
        <PlanrButton type="greenish" size="small" onClick={doApply} disabled={applyDisabled}>
          Применить
        </PlanrButton>
      </div>
    </div>
  );

  const previewPanel = (
    <div className={styles.previewPanel}>
      {store.previewing && (
        <div className={styles.center}>
          <Spinner size={20} /> <span>Сравнение с 1С…</span>
        </div>
      )}

      {!preview && !store.previewing && (
        <div className={styles.hint}>Нажмите «Предпросмотр», чтобы сравнить данные 1С с приложением.</div>
      )}

      {preview && !store.previewing && (
        <div className={styles.preview}>
          {/* одна подсказка: что значит каждый статус и что сделает импорт, последней строкой - предупреждение */}
          <div className={styles.legend}>
            {LEGEND[entityKey].map((row) => (
              <div key={row.status}>
                <span className={styles[`legend_${row.status}`]}>
                  {row.status === "unresolved" ? "Не сопоставлено" : statusLabel[row.status]}
                </span>{" "}
                — {row.text}
              </div>
            ))}
            {isEmployees && (missingLinks.length > 0 || newWithoutPosition > 0) && (
              <div className={styles.legendWarning}>
                {missingLinks.length > 0
                  ? `Должность и отдел из 1С сейчас не подставятся: в приложении нет ${missingLinks
                      .map((m) => m.gen)
                      .join(" и ")}, связанных с 1С. Сначала импортируйте ${missingLinks
                      .map((m) => m.acc)
                      .join(" и ")} или выберите должность в строке.`
                  : `У новых сотрудников (${newWithoutPosition}) должность из 1С не найдена — выберите её в строке.`}
              </div>
            )}
          </div>
          <div className={styles.toolbarRow}>
            <div className={styles.searchRow}>
              <Search query={search} onSearch={setSearch} placeholder="Поиск по данным…" style={{ marginLeft: 0 }} />
            </div>
            <div className={styles.selectButtons}>
              <PlanrButton
                type="lightblueish"
                size="small"
                onClick={() => setIncludeAll(selectableIds, true)}
                disabled={selectableIds.length === 0 || allVisibleSelected}
              >
                Выбрать все
              </PlanrButton>
              <PlanrButton
                type="graybtn"
                size="small"
                onClick={() => setIncludeAll(selectableIds, false)}
                disabled={selectedVisible === 0}
              >
                Снять все
              </PlanrButton>
            </div>
          </div>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>
                    {selectableIds.length > 0 && (
                      <GeneralIcon
                        type={allVisibleSelected ? "general-checkbox" : "general-zero-checkbox"}
                        className={styles.checkIcon}
                        title={allVisibleSelected ? "Снять все" : "Выбрать все"}
                        onClick={() => setIncludeAll(selectableIds, !allVisibleSelected)}
                      />
                    )}
                  </th>
                  <th>{isEmployees ? "ФИО в 1С" : isStaff ? "Ставки в 1С" : "Наименование в 1С"}</th>
                  {isEmployees && <th style={{ width: "100px" }}>Дата рожд.</th>}
                  <th style={{ width: isEmployees ? "160px" : "170px" }}>Статус</th>
                  <th style={{ width: "300px" }}>
                    {isEmployees ? "Сотрудник в приложении" : isStaff ? "Сейчас в приложении" : "Привязать к записи в приложении"}
                  </th>
                  {isEmployees && <th style={{ width: "255px" }}>Должность в приложении</th>}
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item, idx) => {
                  const isImportable = isImportableItem(item);
                  const decision = item.externalId ? decisions[item.externalId] : undefined;

                  return (
                    <tr
                      key={idx}
                      className={isImportable ? styles.rowClickable : styles.rowMuted}
                      onClick={isImportable ? () => toggleInclude(item.externalId as string) : undefined}
                    >
                      <td>
                        {isImportable && (
                          // клик всплывает до строки - она и переключает отметку
                          <GeneralIcon
                            type={decision && decision.include ? "general-checkbox" : "general-zero-checkbox"}
                            className={styles.checkIcon}
                          />
                        )}
                      </td>
                      <td>
                        {item.status === "onlyInRost" && item.targetId ? (
                          <div onClick={(e) => e.stopPropagation()}>
                            <SimpleSelect
                              className={`full-width-select ${Classes.FILL}`}
                              activeItem={null}
                              items={sourceItems}
                              itemRenderer={renderSingleOption}
                              onItemSelect={(i: SelectItem) => bindFromRost(i.id, item.targetId as string)}
                              popoverProps={{ rootBoundary: "viewport" }}
                              inputProps={{ placeholder: "Запись 1С" }}
                              filterable={true}
                              itemListPredicate={limitBind}
                              disabled={sourceItems.length === 0}
                            >
                              <DefaultSelectedOption option={null} what="Выбрать из 1С" />
                            </SimpleSelect>
                          </div>
                        ) : (
                          item.sourceName || "—"
                        )}
                        {item.isDeleted && (
                          <span className={styles.deleted}>
                            {entityKey === "positions" ? " (удалена в 1С)" : " (удалён в 1С)"}
                          </span>
                        )}
                      </td>
                      {isEmployees && <td style={{ whiteSpace: "nowrap" }}>{item.birthday || "—"}</td>}
                      <td>
                        {item.problem ? (
                          <span className={styles.statusOnlyRost}>Не сопоставлено</span>
                        ) : (
                          <span className={styles[STATUS_CLASS[effectiveStatus(item)]]}>{statusLabel[effectiveStatus(item)]}</span>
                        )}
                      </td>
                      <td>
                        {isStaff ? (
                          <span className={styles.targetName}>{item.targetName || "—"}</span>
                        ) : item.status === "linked" ? (
                          renderTarget(item.targetId, item.targetName)
                        ) : isImportable ? (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            title={findItem(bindItems, decision ? decision.targetId : "")?.label}
                          >
                            <SimpleSelect
                              className={`full-width-select ${Classes.FILL}`}
                              activeItem={findItem(bindItems, decision ? decision.targetId : "")}
                              items={bindItems}
                              itemRenderer={renderSingleOption}
                              onItemSelect={(i: SelectItem) => setTarget(item.externalId as string, i.id)}
                              popoverProps={{ rootBoundary: "viewport" }}
                              inputProps={{ placeholder: bindPlaceholder }}
                              filterable={true}
                              itemListPredicate={limitBind}
                            >
                              <DefaultSelectedOption option={findItem(bindItems, decision ? decision.targetId : "")} />
                            </SimpleSelect>
                          </div>
                        ) : (
                          renderTarget(item.targetId, item.targetName)
                        )}
                      </td>
                      {isEmployees && (
                        <td>
                          {isImportable && decision && decision.targetId ? (
                            renderCurrentPosition(decision.targetId)
                          ) : isImportable ? (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              title={findItem(positionItems, decision ? decision.positionTargetId : "")?.label}
                            >
                              <SimpleSelect
                                className={`full-width-select ${Classes.FILL}`}
                                activeItem={findItem(positionItems, decision ? decision.positionTargetId : "")}
                                items={positionItems}
                                itemRenderer={renderSingleOption}
                                onItemSelect={(i: SelectItem) => setPosition(item.externalId as string, i.id)}
                                popoverProps={{ rootBoundary: "viewport" }}
                                inputProps={{ placeholder: "Должность" }}
                                filterable={true}
                                itemListPredicate={limitBind}
                              >
                                <DefaultSelectedOption
                                  option={findItem(positionItems, decision ? decision.positionTargetId : "")}
                                />
                              </SimpleSelect>
                              {renderUnit(item, decision ? decision.positionTargetId : "")}
                            </div>
                          ) : item.status === "onlyInRost" && item.targetId ? (
                            renderCurrentPosition(item.targetId)
                          ) : (
                            "—"
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  // Чипы сводки - фильтры по статусу; стоят в шапке окна рядом с вкладками
  const summaryChips = isStaff ? (
    <>
      {renderChip("new", styles.statusNew, "Новые ставки", counts.new)}
      {renderChip("candidate", styles.statusCandidate, "Изменится", counts.candidate)}
      {renderChip("linked", styles.statusLinked, "Совпадает", counts.linked)}
      {renderChip("unresolved", styles.statusOnlyRost, "Не сопоставлено", unresolved)}
    </>
  ) : (
    <>
      {renderChip("new", styles.statusNew, statusLabel.new, counts.new)}
      {renderChip("candidate", styles.statusCandidate, statusLabel.candidate, counts.candidate)}
      {renderChip("linked", styles.statusLinked, statusLabel.linked, counts.linked)}
      {renderChip("onlyInRost", styles.statusOnlyRost, statusLabel.onlyInRost, counts.onlyInRost)}
    </>
  );

  const hasTabs = !store.loadingResources && store.resources.length > 0;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={closeDialog}
      backdropClassName="standard"
      className={`${styles.dialog} figma-dialog`}
      style={{ width: "1120px" }}
    >
      {/* своя шапка: вкладки вместо заголовка, справа от них - фильтры по статусу; пока вкладок нет - заголовок */}
      <div className={`${Classes.DIALOG_HEADER} ${styles.header}`}>
        {hasTabs ? (
          <Tabs id="onec-import-tabs" selectedTabId={tab} onChange={(t) => setTab(t as string)} large={true}>
            <Tab id="preview" title="Предпросмотр и импорт" />
            <Tab id="settings" title="Настройки импорта" />
          </Tabs>
        ) : (
          <div className={Classes.HEADING}>{title}</div>
        )}
        {hasTabs && tab === "preview" && preview && !store.previewing && (
          <div className={`${styles.summary} ${styles.headerChips}`}>{summaryChips}</div>
        )}
        <GeneralIcon type="general-cross-big" className={styles.close} title="Закрыть" onClick={closeDialog} />
      </div>
      <div className={Classes.DIALOG_BODY}>
        {store.loadingResources && (
          <div className={styles.center}>
            <Spinner size={24} /> <span>Загрузка каталогов 1С…</span>
          </div>
        )}

        {notConfigured && (
          <div className={styles.warning}>
            Не удалось получить список каталогов 1С. Проверьте адрес, логин и пароль в разделе «Настройки → Интеграция с
            1С».
          </div>
        )}

        {hasTabs && (tab === "preview" ? previewPanel : mappingPanel)}
      </div>

      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <PlanrButton type="graybtn" size="small" onClick={closeDialog}>
            Отмена
          </PlanrButton>
          <PlanrButton
            type="greenish"
            size="small"
            onClick={doImport}
            disabled={!preview || !canImport || store.importing || selectedCount === 0}
            title={canImport ? undefined : "Импорт для этой сущности пока не поддерживается"}
          >
            {selectedCount > 0 ? `Импортировать (${selectedCount})` : "Импортировать"}
          </PlanrButton>
        </div>
      </div>
    </Dialog>
  );
};

export const OneCImportDialog = observer(OneCImportDialogBase);
